import { discountAmount } from "@rutamagica/contracts";
import { CalendarDays, Users } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { DateBox } from "../../components/DateBox";
import { Button, Dialog, Field, InlineNotice } from "../../components/ui";
import { crmApi } from "../../data/crm";
import { useErpData } from "../../data/DataProvider";
import { formatDate, mexicoDateKey } from "../../data/format";
import type { Booking } from "../../data/types";
import { dateHoldsApi, type CaseDossier, type CommerceQuote, type DateConflict } from "../../data/workspace";
import { money } from "./shared";

const dayAfter = (date: string) => new Date(Date.parse(`${date}T12:00:00Z`) + 86400000).toISOString().slice(0, 10);
const count = (value: string) => (value.trim() === "" ? null : Math.max(0, Math.round(Number(value))));

/**
 * A school visit priced for another number of students, as WordPress prices it: the first line is
 * per student, the other lines stay, and the quote's discount applies again to the new subtotal.
 */
export function schoolTotal(quote: Pick<CommerceQuote, "items" | "discount">, students: number): number {
  let first = true;
  let subtotal = 0;
  for (const item of quote.items ?? []) {
    if (!(item.unit_price > 0)) continue;
    subtotal += Math.round((first ? students : item.quantity) * item.unit_price * 100) / 100;
    first = false;
  }
  return Math.round((subtotal - discountAmount(quote.discount ?? null, subtotal)) * 100) / 100;
}

/**
 * Change a booking the customer already has: another day or time, or (school visits) a different
 * group. Free, until the day before the visit. Payments stay; the case's sale follows the change.
 */
export function BookingChanges({ booking, dossier, onDone }: { booking: Booking; dossier: CaseDossier; onDone: (message: string) => void }) {
  const { hasPermission } = useAuth();
  const { executeCommand } = useErpData();
  const [open, setOpen] = useState<"" | "date" | "group">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const today = mexicoDateKey();
  const changeable = Boolean(booking.date) && today < booking.date && ["holding", "pending_payment", "confirmed"].includes(booking.status);
  const quote = (dossier.quotes ?? []).find((item) => String(item.booking_id ?? "") === booking.id) ?? (dossier.quotes ?? []).find((item) => item.status === "converted");
  const school = booking.service === "school";
  const summary = (dossier.bookings?.[0]?.payment_summary ?? {}) as { paid_amount?: number; contract_total?: number };
  const paid = Number(summary.paid_amount ?? 0);
  const opportunity = dossier.opportunity;

  // Date change.
  const [date, setDate] = useState("");
  const [time, setTime] = useState(booking.time.slice(0, 5));
  const [conflicts, setConflicts] = useState<DateConflict[]>([]);
  useEffect(() => {
    if (open !== "date" || !school || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setConflicts([]); return; }
    let active = true;
    dateHoldsApi.conflicts(date, dossier.key).then((result) => { if (active) setConflicts(result.data); }, () => { if (active) setConflicts([]); });
    return () => { active = false; };
  }, [open, school, date, dossier.key]);

  // Group change (school visits).
  const [students, setStudents] = useState(String(booking.guests || ""));
  const [teachers, setTeachers] = useState(() => String(opportunity?.teachers_count ?? quote?.school?.teachers ?? ""));
  const [companions, setCompanions] = useState(() => String(opportunity?.companions_count ?? quote?.school?.companions ?? ""));
  const [menu, setMenu] = useState(quote?.school?.menu ?? "");
  const studentCount = Math.round(Number(students));
  const newTotal = quote && studentCount > 0 ? schoolTotal(quote, studentCount) : null;
  const oldTotal = Number(summary.contract_total ?? quote?.total ?? booking.total ?? 0);

  const start = (target: "date" | "group") => {
    setError("");
    setDate("");
    setTime(booking.time.slice(0, 5));
    setStudents(String(booking.guests || ""));
    setOpen(target);
  };
  // The sale keeps the case's one record of date and group (header, documents, calendar).
  const syncSale = async (details: Record<string, unknown>) => {
    if (!opportunity?.id) return;
    await crmApi.updateOpportunityDetails(String(opportunity.id), { expected_version: Number(opportunity.version), ...details }).catch(() => undefined);
  };
  const submitDate = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError("Elige la nueva fecha."); return; }
    setBusy(true);
    setError("");
    try {
      await executeCommand({ type: "booking.reschedule", aggregateId: booking.id, expectedVersion: booking.version, payload: { date, ...(time ? { time } : {}) } });
      await syncSale({ requested_date: date, requested_date_precision: "day" });
      setOpen("");
      onDone(`Fecha cambiada al ${formatDate(date)}${time ? ` a las ${time}` : ""}. Los pagos se conservan; envíale su confirmación nueva desde Pagos y comprobantes.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No se pudo cambiar la fecha.");
    } finally {
      setBusy(false);
    }
  };
  const submitGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (!(studentCount > 0)) { setError("Escribe el número de alumnos."); return; }
    setBusy(true);
    setError("");
    try {
      await executeCommand({
        type: "booking.change_group",
        aggregateId: booking.id,
        expectedVersion: booking.version,
        payload: { students: studentCount, teachers: count(teachers), companions: count(companions), menu: menu.trim() },
      });
      await syncSale({ people_count: studentCount, teachers_count: count(teachers), companions_count: count(companions) });
      setOpen("");
      const rest = newTotal === null ? null : newTotal - paid;
      onDone(
        `Grupo cambiado a ${studentCount} alumnos.` +
          (newTotal === null ? "" : ` Total ${money(newTotal)}.`) +
          (rest === null ? "" : rest > 0 ? ` Resta ${money(rest)}.` : rest < 0 ? ` Saldo a favor del cliente: ${money(-rest)}.` : " Pagado por completo."),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No se pudo cambiar el grupo.");
    } finally {
      setBusy(false);
    }
  };

  if (!hasPermission("bookings.edit")) return null;
  return (
    <div className="booking-changes">
      <div className="case-actions">
        <Button variant="secondary" icon={CalendarDays} disabled={!changeable} onClick={() => start("date")}>Cambiar fecha</Button>
        {school && quote && <Button variant="secondary" icon={Users} disabled={!changeable} onClick={() => start("group")}>Cambiar grupo</Button>}
      </div>
      {!changeable && booking.date && <p className="report-explainer">Los cambios se pueden hacer hasta un día antes de la visita.</p>}
      <Dialog open={open === "date"} size="compact" title="Cambiar fecha" eyebrow={booking.reference} onClose={() => setOpen("")}>
        <form className="booking-action-form" onSubmit={(event) => void submitDate(event)}>
          <p>Hoy está para el <strong>{formatDate(booking.date)}</strong>{booking.time ? ` a las ${booking.time.slice(0, 5)}` : ""}. El cambio no tiene costo y los pagos se conservan.</p>
          <DateBox label="Nueva fecha" name="reschedule_date" value={date} min={dayAfter(today)} onChange={setDate} />
          <Field label="Horario" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          {conflicts.length > 0 && (
            <InlineNotice tone="warning">
              Ese día ya hay {conflicts.length === 1 ? "otro grupo escolar" : `${conflicts.length} grupos escolares`}: {conflicts.map((item) => `${item.name}${item.people ? ` (${item.people})` : ""}`).join("; ")}.
            </InlineNotice>
          )}
          {error && <p role="alert" className="case-sheet__error">{error}</p>}
          <Button type="submit" icon={CalendarDays} disabled={busy || !date}>{busy ? "Cambiando…" : "Cambiar fecha"}</Button>
        </form>
      </Dialog>
      <Dialog open={open === "group"} size="compact" title="Cambiar grupo" eyebrow={booking.reference} onClose={() => setOpen("")}>
        <form className="booking-action-form" onSubmit={(event) => void submitGroup(event)}>
          <div className="booking-action-form__split">
            <Field label="Alumnos" type="number" min="1" max="5000" value={students} onChange={(event) => setStudents(event.target.value)} required />
            <Field label="Profesores" type="number" min="0" max="1000" value={teachers} onChange={(event) => setTeachers(event.target.value)} hint="Cortesía" />
            <Field label="Acompañantes" type="number" min="0" max="1000" value={companions} onChange={(event) => setCompanions(event.target.value)} />
          </div>
          <Field label="Menú" value={menu} maxLength={190} onChange={(event) => setMenu(event.target.value)} />
          {newTotal !== null && (
            <dl className="booking-changes__money">
              <div><dt>Total</dt><dd>{newTotal !== oldTotal ? <><s>{money(oldTotal)}</s> {money(newTotal)}</> : money(newTotal)}</dd></div>
              <div><dt>Pagado</dt><dd>{money(paid)}</dd></div>
              {newTotal >= paid ? (
                <div><dt>Resta</dt><dd>{money(newTotal - paid)}</dd></div>
              ) : (
                <div className="booking-changes__credit"><dt>Saldo a favor</dt><dd>{money(paid - newTotal)}</dd></div>
              )}
            </dl>
          )}
          {newTotal !== null && newTotal < paid && (
            <p className="report-explainer">El cliente pagó de más: devuélvele la diferencia desde WooCommerce o déjala a su favor.</p>
          )}
          {error && <p role="alert" className="case-sheet__error">{error}</p>}
          <Button type="submit" icon={Users} disabled={busy || !(studentCount > 0)}>{busy ? "Guardando…" : "Guardar grupo"}</Button>
        </form>
      </Dialog>
    </div>
  );
}
